import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Upload, Download, Trash2, Shield, Image as ImageIcon, Pencil, Plus, Loader2, Ban, Save } from 'lucide-react';
import api from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/api';
import AddProductDialog from '@/components/AddProductDialog';
import ProductImageManager from '@/components/ProductImageManager';
import ProductEditDialog from '@/components/ProductEditDialog';
import CatalogsTab from '@/pages/AdminCatalogsTab';
import { BulkSupplierBar } from '@/components/BulkSupplierBar';
import { useAuth } from '@/lib/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

function StatusBadge({ status }) {
  return <span className={`badge badge--${status || 'submitted'}`}>{status}</span>;
}

function ApplicationsTab() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState({});
  const [discounts, setDiscounts] = useState({});

  const load = async () => {
    const { data } = await api.get('/admin/users', { params: { status: 'pending' } });
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (u) => {
    const discount = parseFloat(discounts[u.id] || '0');
    setBusy({ ...busy, [u.id]: true });
    try {
      await api.patch(`/admin/users/${u.id}`, { status: 'active', discount_percent: discount });
      toast.success(`Approved ${u.business_name}`);
      load();
    } finally {
      setBusy({ ...busy, [u.id]: false });
    }
  };
  const reject = async (u) => {
    setBusy({ ...busy, [u.id]: true });
    try {
      await api.patch(`/admin/users/${u.id}`, { status: 'rejected' });
      toast.success(`Rejected ${u.business_name}`);
      load();
    } finally {
      setBusy({ ...busy, [u.id]: false });
    }
  };

  if (users.length === 0) {
    return <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">No pending applications.</div>;
  }

  return (
    <div className="space-y-4">
      {users.map((u) => (
        <div key={u.id} className="border border-neutral-200 p-5 grid md:grid-cols-[1fr_auto] gap-4 items-start">
          <div>
            <div className="flex items-center gap-3">
              <p className="serif text-xl">{u.business_name}</p>
              <StatusBadge status={u.status} />
            </div>
            <p className="sans text-sm text-neutral-500 mt-1">{u.contact_name} · {u.email} · {u.phone || 'no phone'}</p>
            <div className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-neutral-600">
              {u.business_type && <p><span className="uppercase tracking-widest text-neutral-500">Type:</span> {u.business_type}</p>}
              {u.license_number && <p><span className="uppercase tracking-widest text-neutral-500">License:</span> {u.license_number}</p>}
              {u.monthly_volume && <p><span className="uppercase tracking-widest text-neutral-500">Monthly:</span> {u.monthly_volume}</p>}
              {u.shipping_address && <p className="md:col-span-2"><span className="uppercase tracking-widest text-neutral-500">Ship to:</span> {u.shipping_address}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-stretch min-w-[240px]">
            <div>
              <label className="ije-label">Discount % off wholesale</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                placeholder="0"
                value={discounts[u.id] ?? ''}
                onChange={(e) => setDiscounts({ ...discounts, [u.id]: e.target.value })}
                className="ije-input"
                data-testid={IDS.admin.discountInput(u.id)}
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => approve(u)} disabled={busy[u.id]} className="btn-primary flex-1" data-testid={IDS.admin.approveUser(u.id)}>
                <Check size={14} /> Approve
              </button>
              <button type="button" onClick={() => reject(u)} disabled={busy[u.id]} className="btn-outline" data-testid={IDS.admin.rejectUser(u.id)}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountsTab() {
  const [users, setUsers] = useState([]);
  const [discounts, setDiscounts] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  const load = async () => {
    const { data } = await api.get('/admin/users');
    setUsers(data.filter((u) => u.role !== 'admin'));
  };
  useEffect(() => { load(); }, []);

  const visible = users.filter((u) => statusFilter === 'all' ? true : u.status === statusFilter);

  const saveDiscount = async (u) => {
    const d = parseFloat(discounts[u.id] ?? u.discount_percent);
    await api.patch(`/admin/users/${u.id}`, { discount_percent: d });
    toast.success('Discount updated');
    load();
  };

  const toggleStatus = async (u) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    if (!window.confirm(`${next === 'disabled' ? 'Disable' : 'Enable'} ${u.business_name}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { status: next });
      toast.success(`Account ${next}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change status'); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.business_name}?\nThis can only be done if the account has no orders, quotes, or invoices — otherwise you'll be asked to disable it instead.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('Account deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account');
    }
  };

  const statusChip = (s) => {
    const map = {
      active: 'bg-green-50 text-green-700 border-green-200',
      disabled: 'bg-red-50 text-red-700 border-red-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      rejected: 'bg-neutral-100 text-neutral-500 border-neutral-200',
    };
    return (
      <span className={`sans text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${map[s] || map.pending}`}>{s}</span>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {['active', 'disabled', 'pending', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`sans text-[10px] tracking-widest uppercase px-3 py-1.5 border transition-colors ${statusFilter === s ? 'border-black bg-neutral-900 text-white' : 'border-neutral-200 text-neutral-500 hover:border-neutral-500'}`}
              data-testid={`accounts-filter-${s}`}
            >
              {s}
            </button>
          ))}
          <span className="sans text-xs text-neutral-500 ml-2">{visible.length}</span>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="btn-primary h-10"
          data-testid="admin-add-customer"
        >
          <Plus size={14} /> Add customer
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">No {statusFilter === 'all' ? '' : statusFilter} accounts. Tap "Add customer" to create one manually.</div>
      ) : (
        <>
        {/* Mobile card list */}
        <div className="md:hidden space-y-3">
          {visible.map((u) => (
            <div key={u.id} className="border border-neutral-200 p-4" data-testid={`admin-account-card-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="serif text-sm">{u.business_name}</p>
                  <p className="sans text-[11px] text-neutral-500 truncate">{u.email}</p>
                  <p className="sans text-[11px] text-neutral-500 mt-0.5">{u.contact_name}</p>
                </div>
                {statusChip(u.status)}
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-100">
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500">Discount</label>
                <input
                  type="number" min={0} max={100} step={0.5}
                  defaultValue={u.discount_percent}
                  onChange={(e) => setDiscounts({ ...discounts, [u.id]: e.target.value })}
                  className="ije-input h-9 w-20"
                  data-testid={IDS.admin.discountInput(u.id)}
                />
                <span className="sans text-xs text-neutral-500">%</span>
                <button type="button" onClick={() => saveDiscount(u)} className="btn-outline h-9 ml-auto" data-testid={IDS.admin.saveDiscount(u.id)}>Save</button>
              </div>
              <div className="grid grid-cols-3 gap-1 mt-2">
                <button type="button" onClick={() => setEditing(u)} className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm" data-testid={`admin-account-edit-${u.id}`}>
                  <Pencil size={14} />
                  <span className="sans text-[9px] tracking-widest uppercase">Edit</span>
                </button>
                <button type="button" onClick={() => toggleStatus(u)} className={`flex flex-col items-center gap-1 py-2 rounded-sm ${u.status === 'active' ? 'text-neutral-700 active:bg-amber-50 active:text-amber-700' : 'text-neutral-700 active:bg-green-50 active:text-green-700'}`} data-testid={`admin-account-toggle-${u.id}`}>
                  {u.status === 'active' ? <Ban size={14} /> : <Check size={14} />}
                  <span className="sans text-[9px] tracking-widest uppercase">{u.status === 'active' ? 'Disable' : 'Enable'}</span>
                </button>
                <button type="button" onClick={() => remove(u)} className="flex flex-col items-center gap-1 py-2 text-neutral-500 active:bg-red-50 active:text-red-600 rounded-sm" data-testid={`admin-account-delete-${u.id}`}>
                  <Trash2 size={14} />
                  <span className="sans text-[9px] tracking-widest uppercase">Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
        {/* Desktop table */}
        <table className="hidden md:table w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200">
              <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Business</th>
              <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Contact</th>
              <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Status</th>
              <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Discount %</th>
              <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} className="border-b border-neutral-100">
                <td className="py-4">
                  <p className="serif">{u.business_name}</p>
                  <p className="sans text-xs text-neutral-500">{u.email}</p>
                </td>
                <td className="sans">{u.contact_name}</td>
                <td>{statusChip(u.status)}</td>
                <td>
                  <input
                    type="number" min={0} max={100} step={0.5}
                    defaultValue={u.discount_percent}
                    onChange={(e) => setDiscounts({ ...discounts, [u.id]: e.target.value })}
                    className="ije-input w-24"
                    data-testid={`${IDS.admin.discountInput(u.id)}-desktop`}
                  />
                </td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button type="button" onClick={() => saveDiscount(u)} className="text-neutral-500 hover:text-black p-1.5" title="Save discount" data-testid={`${IDS.admin.saveDiscount(u.id)}-desktop`}>
                      <Save size={15} />
                    </button>
                    <button type="button" onClick={() => setEditing(u)} className="text-neutral-500 hover:text-black p-1.5" title="Edit account" data-testid={`admin-account-edit-${u.id}-desktop`}>
                      <Pencil size={15} />
                    </button>
                    <button type="button" onClick={() => toggleStatus(u)} className="text-neutral-500 hover:text-amber-600 p-1.5" title={u.status === 'active' ? 'Disable' : 'Enable'} data-testid={`admin-account-toggle-${u.id}-desktop`}>
                      {u.status === 'active' ? <Ban size={15} /> : <Check size={15} />}
                    </button>
                    <button type="button" onClick={() => remove(u)} className="text-neutral-500 hover:text-red-600 p-1.5" title="Delete" data-testid={`admin-account-delete-${u.id}-desktop`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <EditCustomerDialog user={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={load} />
    </div>
  );
}

function EditCustomerDialog({ user, onOpenChange, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email || '',
        contact_name: user.contact_name || '',
        business_name: user.business_name || '',
        phone: user.phone || '',
        shipping_address: user.shipping_address || '',
        license_number: user.license_number || '',
        business_type: user.business_type || '',
        monthly_volume: user.monthly_volume || '',
        discount_percent: String(user.discount_percent ?? 0),
      });
      setPassword('');
    }
  }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.email.trim() || !form.contact_name.trim() || !form.business_name.trim()) {
      toast.error('Email, contact, and business are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        contact_name: form.contact_name.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim() || null,
        shipping_address: form.shipping_address.trim() || null,
        license_number: form.license_number.trim() || null,
        business_type: form.business_type.trim() || null,
        monthly_volume: form.monthly_volume.trim() || null,
        discount_percent: Math.max(0, Number(form.discount_percent) || 0),
      };
      if (password.trim()) payload.password = password.trim();
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success('Account updated');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes');
    } finally { setBusy(false); }
  };

  if (!user || !form) return null;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-16px)] max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]"
        data-testid="edit-customer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">Edit account</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500 truncate">
            {user.business_name} · {user.email}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <TextField label="Business name *" value={form.business_name} onChange={upd('business_name')} testid="edit-cust-business" />
          <TextField label="Contact name *" value={form.contact_name} onChange={upd('contact_name')} testid="edit-cust-contact" />
          <TextField label="Email *" type="email" value={form.email} onChange={upd('email')} testid="edit-cust-email" />
          <TextField label="Phone" value={form.phone} onChange={upd('phone')} testid="edit-cust-phone" />
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Shipping address</label>
            <textarea rows={2} value={form.shipping_address} onChange={upd('shipping_address')} className="ije-input w-full p-3 resize-none" data-testid="edit-cust-ship" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="License #" value={form.license_number} onChange={upd('license_number')} testid="edit-cust-license" />
            <TextField label="Business type" value={form.business_type} onChange={upd('business_type')} testid="edit-cust-type" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Monthly volume" value={form.monthly_volume} onChange={upd('monthly_volume')} testid="edit-cust-volume" />
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Discount %</label>
              <input type="number" min={0} max={100} step={0.5} value={form.discount_percent} onChange={upd('discount_percent')} className="ije-input h-10 w-full" data-testid="edit-cust-discount" />
            </div>
          </div>
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">New password (leave blank to keep current)</label>
            <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="ije-input h-10 w-full" data-testid="edit-cust-password" />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <button type="button" onClick={() => onOpenChange(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto" data-testid="edit-cust-cancel">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="edit-cust-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCustomerDialog({ open, onOpenChange, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: '', contact_name: '', business_name: '', phone: '',
    shipping_address: '', license_number: '', business_type: '',
    discount_percent: '0', password: '',
  });
  const [generated, setGenerated] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ email: '', contact_name: '', business_name: '', phone: '',
        shipping_address: '', license_number: '', business_type: '',
        discount_percent: '0', password: '' });
      setGenerated(null);
    }
  }, [open]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.email.trim() || !form.contact_name.trim() || !form.business_name.trim()) {
      toast.error('Email, contact, and business are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        contact_name: form.contact_name.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim() || null,
        shipping_address: form.shipping_address.trim() || null,
        license_number: form.license_number.trim() || null,
        business_type: form.business_type.trim() || null,
        discount_percent: Math.max(0, Number(form.discount_percent) || 0),
        password: form.password.trim() || null,
        status: 'active',
      };
      const { data } = await api.post('/admin/users', payload);
      toast.success('Customer created');
      if (data._generated_password) {
        setGenerated({ email: data.email, pw: data._generated_password });
      } else {
        onCreated?.();
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create customer');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-16px)] max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]"
        data-testid="add-customer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">Add customer</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">
            Creates an active wholesale account
          </p>
        </DialogHeader>

        {generated ? (
          <div className="space-y-4">
            <div className="border border-[color:var(--ije-pink)] bg-[color:var(--ije-pink-tint)]/40 p-4">
              <p className="eyebrow" style={{ color: 'var(--ije-pink-dark)' }}>Auto-generated password</p>
              <p className="sans text-sm text-neutral-700 mt-1">Copy this now — it won't be shown again. Share it with the customer securely.</p>
              <div className="mt-3 space-y-1">
                <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">Email</p>
                <p className="font-mono text-sm bg-white p-2 border border-neutral-200 break-all">{generated.email}</p>
                <p className="sans text-[10px] tracking-widest uppercase text-neutral-500 mt-2">Password</p>
                <p className="font-mono text-sm bg-white p-2 border border-neutral-200 select-all break-all">{generated.pw}</p>
              </div>
            </div>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(`${generated.email} / ${generated.pw}`); toast.success('Copied to clipboard'); }} className="btn-outline h-10 w-full" data-testid="add-customer-copy">
              Copy email + password
            </button>
            <button type="button" onClick={() => { onCreated?.(); onOpenChange(false); }} className="btn-primary h-11 w-full" data-testid="add-customer-done">
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <TextField label="Business name *" value={form.business_name} onChange={upd('business_name')} testid="add-cust-business" />
            <TextField label="Contact name *" value={form.contact_name} onChange={upd('contact_name')} testid="add-cust-contact" />
            <TextField label="Email *" type="email" value={form.email} onChange={upd('email')} testid="add-cust-email" />
            <TextField label="Phone" value={form.phone} onChange={upd('phone')} testid="add-cust-phone" />
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Shipping address</label>
              <textarea rows={2} value={form.shipping_address} onChange={upd('shipping_address')} className="ije-input w-full p-3 resize-none" data-testid="add-cust-ship" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="License #" value={form.license_number} onChange={upd('license_number')} testid="add-cust-license" />
              <TextField label="Business type" value={form.business_type} onChange={upd('business_type')} testid="add-cust-type" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Discount %</label>
                <input type="number" min={0} max={100} step={0.5} value={form.discount_percent} onChange={upd('discount_percent')} className="ije-input h-10 w-full" data-testid="add-cust-discount" />
              </div>
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Password (optional)</label>
                <input type="text" value={form.password} onChange={upd('password')} placeholder="Auto-generate" className="ije-input h-10 w-full" data-testid="add-cust-password" />
              </div>
            </div>
          </div>
        )}

        {!generated && (
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto" data-testid="add-customer-cancel">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={busy} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="add-customer-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create account
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, testid, ...rest }) {
  return (
    <div>
      <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">{label}</label>
      <input className="ije-input h-10 w-full" data-testid={testid} {...rest} />
    </div>
  );
}

function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();
  const load = async () => {
    const { data } = await api.get('/admin/quotes');
    setQuotes(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (q, status) => {
    await api.patch(`/admin/quotes/${q.id}`, { status });
    toast.success(`${q.quote_number} → ${status}`);
    load();
  };

  const convert = async (q) => {
    if (!window.confirm(`Convert ${q.quote_number} into a real Order?\nThe order will start as Approved so you can invoice + push to DJ immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/convert-to-order`);
      toast.success(`Order ${data.order_number} created`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not convert quote');
    }
  };

  if (quotes.length === 0) {
    return <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">No legacy quotes. Everything lives under Orders.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="border border-neutral-200 bg-neutral-50 p-3">
        <p className="sans text-[11px] text-neutral-600">
          <strong className="tracking-widest uppercase text-[10px] text-[color:var(--ije-pink-dark)] mr-1">Heads up:</strong>
          Quotes are a legacy RFQ flow. Tap <em>Convert to order</em> to move a quote into the modern Orders pipeline (approval, invoice, push to Doc Johnson).
        </p>
      </div>
      {quotes.map((q) => (
        <div key={q.id} className="border border-neutral-200 p-5" data-testid={IDS.admin.quoteRow(q.id)}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3">
                <p className="serif text-xl">{q.quote_number}</p>
                <StatusBadge status={q.status} />
              </div>
              <p className="sans text-xs text-neutral-500 mt-1">
                {q.business_name} · {q.user_email} · {new Date(q.created_at).toLocaleString()}
              </p>
              {q.converted_order_id && (
                <p className="sans text-[11px] text-[color:var(--ije-pink-dark)] tracking-widest uppercase mt-1">
                  Converted → order {q.converted_order_id.slice(0, 8)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="serif text-xl mr-2">${Number(q.total).toFixed(2)}</p>
              {!q.converted_order_id && (
                <button
                  type="button"
                  onClick={() => convert(q)}
                  className="btn-primary h-9"
                  data-testid={`convert-quote-${q.id}`}
                >
                  Convert to order
                </button>
              )}
              <select
                value={q.status}
                onChange={(e) => updateStatus(q, e.target.value)}
                className="ije-input py-2"
                data-testid={IDS.admin.statusSelect(q.id)}
              >
                <option value="submitted">Submitted</option>
                <option value="quoted">Quoted</option>
                <option value="confirmed">Confirmed</option>
                <option value="converted" disabled>Converted</option>
              </select>
            </div>
          </div>
          <table className="w-full text-sm mt-4">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-2 text-left font-medium">Item</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-2 text-right font-medium">Qty</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-2 text-right font-medium">Unit</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-2 text-right font-medium">Line</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((it, i) => (
                <tr key={i} className="border-b border-neutral-100">
                  <td className="py-2">
                    <p>{it.name}</p>
                    <p className="text-xs text-neutral-500">{it.sku}{it.variant ? ` · ${it.variant}` : ''}</p>
                  </td>
                  <td className="text-right">{it.qty}</td>
                  <td className="text-right">${Number(it.your_unit_price).toFixed(2)}</td>
                  <td className="text-right">${Number(it.line_total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(q.po_number || q.shipping_address || q.notes) && (
            <div className="mt-3 text-xs text-neutral-600 space-y-1">
              {q.po_number && <p><span className="uppercase tracking-widest text-neutral-500">PO:</span> {q.po_number}</p>}
              {q.shipping_address && <p><span className="uppercase tracking-widest text-neutral-500">Ship to:</span> {q.shipping_address}</p>}
              {q.notes && <p><span className="uppercase tracking-widest text-neutral-500">Notes:</span> {q.notes}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [imageEditProduct, setImageEditProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('supplier') || '';
  });
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSel = (pid) => setSelectedIds((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]));
  const allShownSelected = products.length > 0 && products.every((p) => selectedIds.includes(p.id));
  const toggleAllShown = () => setSelectedIds(allShownSelected ? [] : products.map((p) => p.id));

  const load = async () => {
    const params = { limit: 500 };
    if (search.trim()) params.q = search.trim();
    if (brandFilter) params.brand = brandFilter;
    if (supplierFilter) params.supplier = supplierFilter;
    const { data } = await api.get('/products', { params });
    setProducts(data.items);
    setTotal(data.total || data.items.length);
    if (data.brands) setBrands(data.brands);
  };
  const loadSync = async () => {
    const { data } = await api.get('/admin/sync-catalog/status');
    setSyncStatus(data);
  };
  const loadSuppliers = async () => {
    try {
      const { data } = await api.get('/admin/suppliers');
      setSuppliers(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadSync(); loadSuppliers(); }, []);
  // Debounce the search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [search, brandFilter, supplierFilter]);

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success('Deleted');
    load();
  };

  const syncDJ = async ({ wipe = false } = {}) => {
    if (wipe && !window.confirm('Wipe non-Doc-Johnson products and re-import full DJ catalog?')) return;
    setSyncing(true);
    const toastId = toast.loading('Doc Johnson sync started in background…');
    try {
      // Fire-and-forget on the server; we poll status for progress
      await api.post('/admin/sync-catalog', { updated_since: null });
      // If wipe requested, clear non-DJ rows client-side via delete calls (rare op)
      if (wipe) {
        const nonDJ = (await api.get('/products', { params: { limit: 2000 } })).data.items
          .filter((p) => p.source !== 'docjohnson');
        for (const p of nonDJ) {
          try { await api.delete(`/products/${p.id}`); } catch (_) {
            /* ignore individual delete errors during wipe */
          }
        }
      }
      // Poll status every 2s for up to 5 min
      let done = false;
      const startedAt = Date.now();
      const initial = await api.get('/admin/sync-catalog/status');
      const priorFinish = initial.data?.last_run_at;
      while (!done && Date.now() - startedAt < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await api.get('/admin/sync-catalog/status');
        if (data?.last_run_at && data.last_run_at !== priorFinish) {
          done = true;
          toast.success(
            `DJ sync complete — ${data.last_run_synced ?? 0} products processed, ${data.last_run_deltas ?? 0} changed`,
            { id: toastId },
          );
          setSyncStatus(data);
          break;
        }
      }
      if (!done) toast.message('Sync still running — check back shortly.', { id: toastId });
      load();
      loadSync();
    } catch (e) {
      toast.error(`Sync failed: ${e.response?.data?.detail || e.message}`, { id: toastId });
    } finally {
      setSyncing(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import complete — created ${data.created}, updated ${data.updated}${data.errors.length ? `, ${data.errors.length} errors` : ''}`);
      load();
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('ije_token');
    const res = await fetch(`${API_BASE}/products/import/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ijehaus_products_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-6 p-4 md:p-5 border border-neutral-200 bg-[color:var(--ije-pink-tint)]/60">
        <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:items-center md:justify-between">
          <div>
            <p className="eyebrow">Doc Johnson Partner Hub</p>
            <p className="serif text-lg mt-1">Catalog sync</p>
            <p className="sans text-xs text-neutral-600 mt-1">
              {syncStatus?.last_run_at ? (
                <>
                  Last <strong>{syncStatus.last_run_kind || 'sync'}</strong> at{' '}
                  <strong>{new Date(syncStatus.last_run_at).toLocaleString()}</strong>
                  {' · '}{syncStatus.last_run_synced ?? 0} processed, {syncStatus.last_run_deltas ?? 0} changed
                  {syncStatus.last_run_status && syncStatus.last_run_status !== 'ok' && (
                    <> · <span style={{ color: 'var(--ije-pink-dark)' }}>status: {syncStatus.last_run_status}</span></>
                  )}
                </>
              ) : (
                'No sync yet — tap below to pull the full DJ catalog.'
              )}
            </p>
            <p className="sans text-[0.65rem] text-neutral-500 mt-2 tracking-[0.18em] uppercase">
              Auto-sync: full nightly 03:00 UTC · delta hourly
            </p>
          </div>
          <div className="grid grid-cols-2 md:flex gap-2 md:items-center md:flex-wrap">
            <button type="button" disabled={syncing} onClick={() => syncDJ({ wipe: false })} className="btn-primary" data-testid="admin-dj-sync">
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
            <button type="button" disabled={syncing} onClick={() => syncDJ({ wipe: true })} className="btn-outline" data-testid="admin-dj-resync">
              Wipe & Re-sync
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 md:flex-wrap">
        <div className="grid grid-cols-2 md:flex gap-2 md:items-center md:gap-3">
          <AddProductDialog onCreated={load} />
          <label className="btn-outline cursor-pointer" data-testid={IDS.admin.csvUpload}>
            <Upload size={14} /> {uploading ? 'Uploading…' : 'Import CSV'}
            <input type="file" accept=".csv" onChange={upload} className="hidden" disabled={uploading} />
          </label>
          <button type="button" onClick={downloadTemplate} className="btn-outline col-span-2 md:col-span-1" data-testid={IDS.admin.csvTemplate}>
            <Download size={14} /> CSV Template
          </button>
        </div>
        <p className="sans text-xs text-neutral-500 md:ml-auto">
          <strong>{products.length}</strong> of {total} shown
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 md:flex-wrap">
        <div className="relative flex-1 md:min-w-[240px] md:max-w-md">
          <input
            data-testid="admin-products-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SKU, or UPC"
            className="ije-input h-11 md:h-10 w-full pl-3 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            data-testid="admin-products-brand-filter"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="ije-input h-11 md:h-10 pr-8 flex-1 md:flex-none"
            style={{ paddingTop: 0, paddingBottom: 0 }}
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select
            data-testid="admin-products-supplier-filter"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="ije-input h-11 md:h-10 pr-8 flex-1 md:flex-none"
            style={{ paddingTop: 0, paddingBottom: 0 }}
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => (
              <option key={s.name} value={s.name}>{s.name} ({s.product_count})</option>
            ))}
          </select>
          {(search || brandFilter || supplierFilter) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setBrandFilter(''); setSupplierFilter(''); }}
              className="sans text-xs uppercase tracking-widest text-neutral-500 hover:text-black shrink-0"
              data-testid="admin-products-clear"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <BulkSupplierBar
        selectedIds={selectedIds}
        suppliers={suppliers}
        onClear={() => setSelectedIds([])}
        onDone={() => { setSelectedIds([]); load(); loadSuppliers(); }}
      />
      <label className="md:hidden flex items-center gap-2 mb-3 sans text-xs uppercase tracking-widest text-neutral-600">
        <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} className="accent-black w-4 h-4" data-testid="admin-products-select-all-mobile" />
        Select all {products.length} shown
      </label>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {products.map((p) => {
          const wholesale = Number(p.wholesale_price ?? p.your_price ?? 0);
          const msrp = Number(p.msrp || 0);
          const hasMargin = p.margin_per_unit != null && p.wholesale_price;
          return (
            <div key={p.id} className="border border-neutral-200 p-4" data-testid={`admin-product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSel(p.id)} className="accent-black w-4 h-4 mt-1 shrink-0" data-testid={`admin-product-select-${p.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="serif text-base leading-tight">{p.name}</p>
                  <p className="sans text-[11px] text-neutral-500 mt-0.5 font-mono truncate">
                    {p.sku}{p.source === 'docjohnson' ? ' · DJ' : ''}
                    {p.brand ? ` · ${p.brand}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-0 shrink-0 -mr-1">
                  <button
                    type="button"
                    onClick={() => setEditProduct(p)}
                    data-testid={`admin-product-edit-${p.id}`}
                    className="p-2 text-neutral-500 active:text-black"
                    title="Edit"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageEditProduct(p)}
                    data-testid={`admin-product-images-${p.id}`}
                    className="p-2 text-neutral-500 active:text-black"
                    title="Images"
                  >
                    <ImageIcon size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => del(p)}
                    data-testid={IDS.admin.productDelete(p.id)}
                    className="p-2 text-neutral-500 active:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-100 grid grid-cols-3 gap-2">
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Wholesale</p>
                  <p className="serif text-sm mt-0.5">${wholesale.toFixed(2)}</p>
                </div>
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">MSRP</p>
                  <p className="serif text-sm mt-0.5">${msrp.toFixed(2)}</p>
                </div>
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Case</p>
                  <p className="serif text-sm mt-0.5">{p.case_pack_size ?? '—'}</p>
                </div>
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Cost</p>
                  <p className="sans text-sm text-neutral-600 mt-0.5">{(p.dj_cost || p.supplier_cost) ? `$${Number(p.dj_cost || p.supplier_cost).toFixed(2)}` : '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Supplier</p>
                  <p className="sans text-sm text-neutral-600 mt-0.5 truncate">{p.supplier || (p.source === 'docjohnson' ? 'Doc Johnson' : '—')}</p>
                </div>
                <div className="col-span-3">
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Margin</p>
                  {hasMargin ? (
                    <p className="mt-0.5">
                      <span className="serif text-sm">${Number(p.margin_per_unit).toFixed(2)}</span>
                      <span className="sans text-xs text-neutral-500 ml-1.5">({Number(p.margin_pct || 0).toFixed(0)}%)</span>
                    </p>
                  ) : <p className="sans text-sm text-neutral-500 mt-0.5">—</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <table className="hidden md:table w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="py-3 pr-2 w-6 text-left">
              <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} className="accent-black w-4 h-4" title={`Select all ${products.length} shown`} data-testid="admin-products-select-all" />
            </th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Product</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Brand</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Supplier</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Cost</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Wholesale</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Margin</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">MSRP</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Case</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.id} className={`border-b border-neutral-100 ${selectedIds.includes(p.id) ? 'bg-[color:var(--ije-pink-tint)]/40' : ''}`}>
              <td className="py-3 pr-2">
                <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSel(p.id)} className="accent-black w-4 h-4" data-testid={`admin-product-select-desktop-${p.id}`} />
              </td>
              <td className="py-3">
                <p className="serif">{p.name}</p>
                <p className="text-xs text-neutral-500">{p.sku}{p.source === 'docjohnson' ? ' · DJ' : ''}</p>
              </td>
              <td className="sans text-neutral-600">{p.brand}</td>
              <td className="sans text-neutral-500 text-xs">{p.supplier || (p.source === 'docjohnson' ? 'Doc Johnson' : '—')}</td>
              <td className="text-right sans text-neutral-500">{(p.dj_cost || p.supplier_cost) ? `$${Number(p.dj_cost || p.supplier_cost).toFixed(2)}` : '—'}</td>
              <td className="text-right">${Number(p.wholesale_price ?? p.your_price ?? 0).toFixed(2)}</td>
              <td className="text-right">
                {p.margin_per_unit != null && p.wholesale_price ? (
                  <>
                    <span className="serif">${Number(p.margin_per_unit).toFixed(2)}</span>
                    <span className="sans text-xs text-neutral-500 ml-1">({Number(p.margin_pct || 0).toFixed(0)}%)</span>
                  </>
                ) : '—'}
              </td>
              <td className="text-right">${Number(p.msrp).toFixed(2)}</td>
              <td className="text-right">{p.case_pack_size}</td>
              <td className="text-right">
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditProduct(p)}
                    data-testid={`admin-product-edit-desktop-${p.id}`}
                    className="text-neutral-500 hover:text-black p-1"
                    title="Edit product details"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageEditProduct(p)}
                    data-testid={`admin-product-images-desktop-${p.id}`}
                    className="text-neutral-500 hover:text-black p-1"
                    title="Manage images"
                  >
                    <ImageIcon size={16} />
                  </button>
                  <button type="button" onClick={() => del(p)} className="text-neutral-500 hover:text-black p-1" title="Delete product">
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ProductImageManager
        product={imageEditProduct}
        open={!!imageEditProduct}
        onOpenChange={(v) => !v && setImageEditProduct(null)}
        onSaved={(newImages) => {
          setProducts((prev) => prev.map((x) =>
            imageEditProduct && x.id === imageEditProduct.id ? { ...x, images: newImages } : x
          ));
          setImageEditProduct((prev) => (prev ? { ...prev, images: newImages } : prev));
        }}
      />

      <ProductEditDialog
        product={editProduct}
        open={!!editProduct}
        onOpenChange={(v) => !v && setEditProduct(null)}
        onSaved={(updated) => {
          setProducts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
        }}
      />
    </div>
  );
}

import AdminOrdersTab from '@/pages/AdminOrdersTab';
import AdminVendorsTab from '@/pages/AdminVendorsTab';
import AdminQuotesTab from '@/pages/AdminQuotesTab';

const TABS = [
  { key: 'applications', label: 'Applications', tid: IDS.admin.tabApplications, C: ApplicationsTab },
  { key: 'accounts', label: 'Accounts', tid: IDS.admin.tabAccounts, C: AccountsTab },
  { key: 'orders', label: 'Orders', tid: 'admin-tab-orders', C: AdminOrdersTab },
  { key: 'quotes-v2', label: 'Quotes', tid: 'admin-tab-quotes-v2', C: AdminQuotesTab },
  { key: 'quotes', label: 'Legacy Quotes', tid: IDS.admin.tabQuotes, C: QuotesTab },
  { key: 'products', label: 'Products', tid: IDS.admin.tabProducts, C: ProductsTab },
  { key: 'vendors', label: 'Vendors', tid: 'admin-tab-vendors', C: AdminVendorsTab },
  { key: 'catalogs', label: 'Catalogs', tid: 'admin-tab-catalogs', C: CatalogsTab },
  { key: 'login-logs', label: 'Login Log', tid: IDS.admin.tabLoginLogs, C: LoginLogsTab, ownerOnly: true },
];

// -----------------------------------------------------------------------------
// LoginLogsTab — visible only to the site owner (admin@ijehaus.com).
// Backend re-checks the email server-side, so hiding the tab is defence in depth.
// -----------------------------------------------------------------------------
function LoginLogsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (emailFilter.trim()) params.email = emailFilter.trim();
      if (statusFilter === 'success') params.success = true;
      if (statusFilter === 'fail') params.success = false;
      const { data } = await api.get('/admin/login-logs', { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load login log');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const REASON_LABEL = {
    ok: 'Success',
    invalid_credentials: 'Wrong password',
    status_rejected: 'Rejected account',
    status_disabled: 'Disabled account',
  };
  const humanizeUA = (ua) => {
    if (!ua) return '';
    if (/iPhone/i.test(ua)) return 'iPhone Safari';
    if (/Android/i.test(ua) && /Chrome/i.test(ua)) return 'Android Chrome';
    if (/iPad/i.test(ua)) return 'iPad Safari';
    if (/Chrome\//i.test(ua) && !/Edg/i.test(ua)) return 'Chrome';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Safari\//i.test(ua)) return 'Safari';
    if (/Edg\//i.test(ua)) return 'Edge';
    return ua.split(' ')[0];
  };
  const formatLoc = (r) => {
    const bits = [r.city, r.region, r.country].filter(Boolean);
    return bits.length ? bits.join(', ') : '—';
  };
  const formatWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div data-testid="admin-login-log-panel">
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-end gap-3 mb-6">
        <div className="flex-1 md:flex-none">
          <p className="eyebrow mb-1">Filter by email</p>
          <input
            data-testid="login-log-email-filter"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="anyone@example.com"
            className="ije-input h-11 md:h-10 w-full md:w-72"
          />
        </div>
        <div>
          <p className="eyebrow mb-1">Result</p>
          <select
            data-testid="login-log-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ije-input h-11 md:h-10 w-full md:w-auto"
          >
            <option value="all">All</option>
            <option value="success">Successful only</option>
            <option value="fail">Failed only</option>
          </select>
        </div>
        <button type="button" onClick={load} disabled={loading} className="btn-primary h-11 md:h-10 w-full md:w-auto" data-testid="login-log-apply">
          {loading ? 'Loading…' : 'Apply'}
        </button>
        <p className="sans text-xs text-neutral-500 md:ml-auto">
          Showing <strong>{rows.length}</strong> of <strong>{total}</strong> events
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">
          {loading ? 'Loading login log…' : 'No login events match those filters.'}
        </div>
      ) : (
        <>
        {/* Mobile card list */}
        <div className="md:hidden space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-neutral-200 p-3" data-testid={`login-log-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="sans text-sm text-neutral-900 truncate">{r.email || '—'}</p>
                  <p className="sans text-[11px] text-neutral-500 mt-0.5">{formatWhen(r.timestamp)}</p>
                </div>
                {r.success ? (
                  <span className="badge badge--in-stock shrink-0">Success</span>
                ) : (
                  <span className="badge badge--rejected shrink-0">{REASON_LABEL[r.reason] || 'Failed'}</span>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-neutral-100 grid grid-cols-2 gap-x-3 gap-y-1">
                {r.role && (
                  <div className="col-span-2">
                    <span className="sans text-[9px] tracking-widest uppercase text-neutral-500 mr-1.5">Role</span>
                    <span className="sans text-[11px] text-neutral-700 uppercase tracking-widest">{r.role}</span>
                  </div>
                )}
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Where</p>
                  <p className="sans text-[11px] text-neutral-700 truncate">{formatLoc(r)}</p>
                </div>
                <div>
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Device</p>
                  <p className="sans text-[11px] text-neutral-700 truncate">{humanizeUA(r.user_agent) || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">IP</p>
                  <p className="sans text-[11px] text-neutral-500 font-mono truncate">{r.ip || '—'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Result</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">IP</th>
                <th className="text-left px-4 py-3">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap sans text-xs text-neutral-700">{formatWhen(r.timestamp)}</td>
                  <td className="px-4 py-3 sans text-xs">
                    <div className="text-neutral-900">{r.email || '—'}</div>
                    {r.role && <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">{r.role}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {r.success ? (
                      <span className="badge badge--in-stock">Success</span>
                    ) : (
                      <span className="badge badge--rejected">{REASON_LABEL[r.reason] || 'Failed'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 sans text-xs text-neutral-700">{formatLoc(r)}</td>
                  <td className="px-4 py-3 sans text-xs text-neutral-500 font-mono">{r.ip || '—'}</td>
                  <td className="px-4 py-3 sans text-xs text-neutral-700">{humanizeUA(r.user_agent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <p className="sans text-[10px] text-neutral-500 mt-3 tracking-widest uppercase">
        Owner-only view · location derived from Cloudflare headers · retention: all events
      </p>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isOwner = (user?.email || '').toLowerCase() === 'admin@ijehaus.com';
  const visibleTabs = TABS.filter((t) => !t.ownerOnly || isOwner);
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'applications';
    const p = new URLSearchParams(window.location.search).get('tab');
    return visibleTabs.some((t) => t.key === p) ? p : 'applications';
  })();
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/admin/stats').then((r) => setStats(r.data));
  }, [tab]);

  const Active = (visibleTabs.find((t) => t.key === tab) || visibleTabs[0]).C;

  return (
    <div className="container-x py-8 md:py-14" data-testid={IDS.admin.root}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="eyebrow">Internal</span>
          <h1 className="h-display text-3xl md:text-5xl mt-2 md:mt-3">Admin.</h1>
        </div>
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4 w-full md:w-auto">
            {[
              ['Pending', stats.pending_applications],
              ['Active', stats.active_accounts],
              ['New Quotes', stats.new_quotes],
              ['Products', stats.active_products],
            ].map(([label, val]) => (
              <div key={label} className="border border-neutral-200 px-3 sm:px-5 py-2 sm:py-3 text-center">
                <p className="serif text-xl sm:text-2xl leading-tight">{val}</p>
                <p className="sans text-[9px] sm:text-xs uppercase tracking-widest text-neutral-500 mt-0.5 sm:mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {stats && (
        <p className="sans text-[11px] md:text-xs text-neutral-500 mt-3 md:mt-4">
          Notifications routed to <span className="link-pink break-all">{stats.notify_email}</span> <span className="hidden sm:inline">(dashboard-only — no email service is wired up)</span>
        </p>
      )}

      {/* Mobile tab selector (< md) */}
      <div className="md:hidden mt-6">
        <label className="ije-label mb-1 block">Section</label>
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value)}
          className="ije-input w-full h-12 text-sm uppercase tracking-widest"
          data-testid="admin-tab-select"
        >
          {visibleTabs.map((t) => (
            <option key={t.key} value={t.key}>
              {t.ownerOnly ? '◈ ' : ''}{t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop tab strip (md+) */}
      <div className="hidden md:flex border-b border-neutral-200 mt-10 gap-8 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button
            type="button"
            key={t.key}
            data-testid={t.tid}
            onClick={() => setTab(t.key)}
            className={`sans text-xs tracking-[0.24em] uppercase pb-4 -mb-px border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              tab === t.key ? 'border-[color:var(--ije-pink)] text-black' : 'border-transparent text-neutral-500 hover:text-black'
            }`}
          >
            {t.ownerOnly && <Shield size={12} strokeWidth={1.6} />}
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 md:mt-8">
        <Active />
      </div>
    </div>
  );
}
